import type { GetPaths } from "./types";

/**
 * GETs the backend only serves with a bearer token (`[Authorize]` with no
 * anonymous door). This is the signed-out fail-fast contract used by `get()`:
 * with no token attached these can only 401, so they are answered locally
 * instead of round-tripping.
 *
 * Deliberately a SEPARATE declaration from the anonymous-browsing stub map
 * (`modules/anonymousBrowsing/anonymousApiStubs.ts`): that map is what
 * anonymous browsing answers locally, this set is the backend's auth surface.
 * The two coincide today (a test in anonymousApiStubs.test.ts pins every stub
 * to this set), but they diverge independently: a new `[Authorize]` GET
 * belongs here whether or not anonymous browsing stubs it.
 *
 * Anonymous checkout is why they now diverge. The priced GETs (`/shop/discounts`,
 * `/memberships/discount-prices`), the promotion reads and
 * `/platform/locations` are stubbed while that setting is off, but the backend genuinely serves them
 * to a signed-out visitor once it is on. Listing them here would make `get()` answer with a local
 * 401 before the request left the browser, so anonymous checkout would show no prices and no offers
 * with nothing in the network tab to explain it. They are deliberately absent; two tests in
 * `anonymousApiStubs.test.ts` pin the divergence in both directions.
 */
export const AUTHENTICATED_ONLY_GET_PATHS: ReadonlySet<GetPaths> = new Set<GetPaths>([
  "/user",
  "/user/preferences",
  "/auth/claims",
  "/multi-platform/list",

  "/rewards",
  "/rewards/referrable",
  "/offers",
  "/visit/count",
  "/user/check-in",
  "/incentives/{platformLocationId}/available-referral-reward",
  "/incentives/{platformLocationId}/active-signup-reward",
  "/incentives/{platformId}/referral-history/{personId}",
  "/incentives/{platformId}/person/{personId}/unacknowledged",
  "/incentives/{platformId}/referral-rewards/{personId}/redeemable",
  "/referral-rewards/{platformId}/location/{locationId}",
  "/gift-cards/transfers/not-acknowledged",

  "/platform-user-memberships",
  "/platform-user-memberships/status",
  "/platform-user-packages",
  "/retailproducts/{platformId}/orders",
  "/retailproducts/{platformId}/earned",

  "/orders/current/details",
  "/orders/current/summary",
  "/orders/current/lineitems",

  "/adonis/top-recommendations",
  "/adonis/top-recommendations/id",
  "/ageless/capabilities",

  "/retailproducts/storefront-settings",
]);

export const isAuthenticatedOnlyGetPath = (path: GetPaths): boolean =>
  AUTHENTICATED_ONLY_GET_PATHS.has(path);
